import core from '@gin-betting/core';
import gamblerSettings, {
    GamblerSettings,
} from '@gin-betting/gambler-settings';
import { omit } from 'lodash';
import { MiddlewareAPI } from 'redux';
import { TypedEpic } from 'redux-observable';
import { REHYDRATE, RehydrateAction } from 'redux-persist';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/concatMap';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/take';

import { SidebarView } from 'app-constants';
import moduleLocator from 'moduleLocator';
import UserDevice from 'services/user-device';
import { Modules, State } from 'types';
import { changeSidebarView } from './actions';
import {
    CHANGE_ODD_FORMAT,
    CHANGE_ODDS_STATISTIC,
    DISABLE_ODD_HISTORY,
    ENABLE_ODD_HISTORY,
    NAME,
    SET_ODDS_CHANGE_MODAL_SHOWN,
    UI_GAMBLER_SETTINGS,
    UISettingKey,
} from './constants';
import { selectSidebarView } from './selectors';
import { IMapKeyValue } from './types';

type StreamModule = Modules['stream']['default'];

type StreamActions = StreamModule['actions'];
type UIActions = typeof import('./actions');

const {
    actions: { changeGamblerSetting: unknownChangeGamblerSetting },
    epics: { makeSyncGamblerSettings },
} = gamblerSettings;
const {
    services: { registerEpic },
    viewport: {
        selectors: { makeViewportSelector },
    },
} = core;

const selectIsMedium = makeViewportSelector('lessThanOrEqual', 'medium');
const changeGamblerSetting: GamblerSettings.MakeChangeGamblerSetting<IMapKeyValue> = unknownChangeGamblerSetting;

type ChangeGamblerSettingsFn = typeof changeGamblerSetting;
type ChangeSidebarView = ReturnType<typeof changeSidebarView>;
type ChangeOddFormat = ReturnType<UIActions['changeOddFormat']>;
type EnableOddHistory = ReturnType<UIActions['enableOddHistory']>;
type DisableOddHistory = ReturnType<UIActions['disableOddHistory']>;
type ChangeOddsStatisticView = ReturnType<UIActions['changeOddsStatisticView']>;
type SetOddsChangeModalShown = ReturnType<UIActions['setOddsChangeModalShown']>;

moduleLocator.get('betslip').subscribe(betslip => {
    const {
        constants: { ADD_BETSLIP_ODD },
        selectors: { selectBetslipOdds },
    } = betslip;

    type BetslipActions = typeof betslip['actions'];
    type AddBetslipOdd = ReturnType<BetslipActions['addBetslipOdd']>;

    const setBetslipView: TypedEpic<AddBetslipOdd, ChangeSidebarView> = (
        action$,
        store
    ) =>
        action$
            .ofType(ADD_BETSLIP_ODD)
            .filter(() => !checkEqualSidebarView(SidebarView.betslip, store))
            .map(() => changeSidebarView({ sidebarView: SidebarView.betslip }));

    const setBetslipViewByRehydrate: TypedEpic<
        RehydrateAction,
        ChangeSidebarView
    > = (action$, store) =>
        action$
            .ofType(REHYDRATE)
            .filter(() => !checkEqualSidebarView(SidebarView.betslip, store))
            .filter(() => selectBetslipOdds(store.getState()).count() > 0)
            .map(() => changeSidebarView({ sidebarView: SidebarView.betslip }));

    const extraEpics: TypedEpic[] = [setBetslipView, setBetslipViewByRehydrate];

    extraEpics.forEach(epic => registerEpic(epic));
});

const makeSetBetslipByStreamOpen = (
    streamModule: StreamModule
): TypedEpic<
    ReturnType<StreamActions['openFullScreenStream']>,
    ReturnType<typeof changeSidebarView>
> => {
    const {
        constants: { OPEN_FULLSCREEN_STREAM },
    } = streamModule;

    return (action$, store) =>
        action$
            .ofType(OPEN_FULLSCREEN_STREAM)
            .filter(() => checkBreakpoint(store))
            .map(() => changeSidebarView({ sidebarView: SidebarView.live }));
};

const makeSetBetslipByStreamClosed = (
    streamModule: StreamModule
): TypedEpic<
    ReturnType<StreamActions['closeFullScreenStream']>,
    ReturnType<typeof changeSidebarView>
> => {
    const {
        constants: { CLOSE_FULLSCREEN_STREAM },
    } = streamModule;

    return (action$, store) =>
        action$
            .ofType(CLOSE_FULLSCREEN_STREAM)
            .filter(
                () =>
                    !checkEqualSidebarView(SidebarView.betslip, store) &&
                    checkIsBetslip(store) &&
                    checkBreakpoint(store)
            )
            .map(() =>
                changeSidebarView({
                    sidebarView: UserDevice.getInstance()?.isDeviceMobile()
                        ? SidebarView.betslip
                        : SidebarView.promo,
                })
            );
};

moduleLocator.get('stream').subscribe(streamModule => {
    const setBetslipByStreamOpen = makeSetBetslipByStreamOpen(streamModule);
    const setBetslipByStreamClosed = makeSetBetslipByStreamClosed(streamModule);

    const extraEpics: TypedEpic[] = [
        setBetslipByStreamOpen,
        setBetslipByStreamClosed,
    ];
    extraEpics.forEach(epic => registerEpic(epic));
});

export const syncEnableOddHistoryGamblerSettings: TypedEpic<
    EnableOddHistory,
    ReturnType<ChangeGamblerSettingsFn>
> = actions$ => {
    return actions$
        .ofType(ENABLE_ODD_HISTORY)
        .map(() => changeGamblerSetting(UISettingKey.SHOW_ODD_HISTORY, true));
};

export const syncDisableOddHistoryGamblerSettings: TypedEpic<
    DisableOddHistory,
    ReturnType<ChangeGamblerSettingsFn>
> = actions$ => {
    return actions$
        .ofType(DISABLE_ODD_HISTORY)
        .map(() => changeGamblerSetting(UISettingKey.SHOW_ODD_HISTORY, false));
};

export const syncOddStatisticGamblerSettings: TypedEpic<
    ChangeOddsStatisticView,
    ReturnType<ChangeGamblerSettingsFn>
> = actions$ => {
    return actions$
        .ofType(CHANGE_ODDS_STATISTIC)
        .map(action => {
            const { show, statisticView } = action.payload;
            const generatedActions: Array<
                ReturnType<ChangeGamblerSettingsFn>
            > = [];

            if (show !== undefined) {
                generatedActions.push(
                    changeGamblerSetting(UISettingKey.SHOW_ODDS_STATISTIC, show)
                );
            }

            if (statisticView !== undefined) {
                generatedActions.push(
                    changeGamblerSetting(
                        UISettingKey.ODD_STATISTIC_VIEW,
                        statisticView
                    )
                );
            }

            return generatedActions;
        })
        .concatMap(action => action);
};

export const syncOddFormatGamblerSettings: TypedEpic<
    ChangeOddFormat,
    ReturnType<ChangeGamblerSettingsFn>
> = actions$ => {
    return actions$
        .ofType(CHANGE_ODD_FORMAT)
        .map(action =>
            changeGamblerSetting(
                UISettingKey.ODD_FORMAT,
                action.payload.oddFormat
            )
        );
};

export const syncOddsChangeModalShownGamblerSettings: TypedEpic<
    SetOddsChangeModalShown,
    ReturnType<ChangeGamblerSettingsFn>
> = actions$ => {
    return actions$
        .ofType(SET_ODDS_CHANGE_MODAL_SHOWN)
        .map(action =>
            changeGamblerSetting(
                UISettingKey.ODDS_CHANGE_MODAL_SHOWN,
                action.payload
            )
        );
};

function checkIsBetslip(store: MiddlewareAPI<State>) {
    const state = store.getState();
    const currSidebarView = selectSidebarView(state);

    return currSidebarView !== SidebarView.betslip;
}

function checkBreakpoint(store: MiddlewareAPI<State>) {
    const state = store.getState();

    const isMedium = selectIsMedium(state, {});

    return !isMedium;
}

function checkEqualSidebarView(
    sidebarView: SidebarView,
    store: MiddlewareAPI<State>
) {
    const state = store.getState();
    const currSidebarView = selectSidebarView(state);

    return sidebarView === currSidebarView;
}

export const makeSyncGamblerSettingsByPlatform = (
    isMobilePlatform: boolean
): ReturnType<typeof makeSyncGamblerSettings> => {
    if (!isMobilePlatform) return makeSyncGamblerSettings(UI_GAMBLER_SETTINGS);

    const mobileOptions = omit(
        UI_GAMBLER_SETTINGS[NAME],
        UISettingKey.SHOW_ODD_HISTORY
    );

    return makeSyncGamblerSettings({
        [NAME]: mobileOptions,
    });
};
